import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { refreshFinancingWidgets } from '@theme/financing-widgets';
import { debounce, prefersReducedMotion, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Builds a selector that survives a section re-render, so focus can be put back.
 * @param {Element | null} element - The element that currently has focus
 * @returns {string | null}
 */
function focusSelectorFor(element) {
  if (!(element instanceof HTMLElement) || element === document.body) return null;

  if (element.id) return `#${CSS.escape(element.id)}`;

  const ref = element.getAttribute('ref');
  const scope = element.closest('[id]');

  if (ref && scope?.id) return `#${CSS.escape(scope.id)} [ref="${ref}"]`;

  return null;
}


/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('category');
    newParameters.delete('category-url');
    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Moves focus between the radios of a facet group without selecting them. Natively a radio is
   * picked the moment an arrow key lands on it, which applies the filter on every keystroke.
   * @param {KeyboardEvent} event - The keydown event
   */
  moveRadioFocus = (event) => {
    const input = event.composedPath()[0];
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    // Enter would submit the form with the value the focused radio has not picked up yet.
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!input.checked) input.click();
      return;
    }

    const backward = event.key === 'ArrowUp' || event.key === 'ArrowLeft';
    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
    if (!backward && !forward) return;

    event.preventDefault();

    const radios = Array.from(this.querySelectorAll('input[type="radio"]:not(:disabled)')).filter(
      (radio) => radio instanceof HTMLInputElement && radio.name === input.name
    );

    const next = radios[(radios.indexOf(input) + (forward ? 1 : -1) + radios.length) % radios.length];
    if (next instanceof HTMLElement) next.focus();
  };

  /**
   * Updates the section
   */
  async #updateSection() {
    // Let synchronous focus moves settle first, such as the sort dropdown closing
    // and handing focus back to its summary, before recording where focus should land.
    await Promise.resolve();

    const focusSelector = focusSelectorFor(document.activeElement);
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      await startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      await sectionRenderer.renderSection(this.sectionId);
    }

    if (focusSelector) {
      const target = document.querySelector(focusSelector);

      if (target instanceof HTMLElement && target !== document.activeElement) target.focus();
    }

    refreshFinancingWidgets();

    // Uncomment this if we want to scroll to the product grid
    // this.#scrollToProductGrid();
  }

  /**
   * Scrolls the first product into view after filters are applied or cleared.
   */
  #scrollToProductGrid() {
    if (document.querySelector('#filters-drawer dialog[open]')) return;

    const resultsList = document.getElementById('ResultsList');
    if (!resultsList) return;

    requestAnimationFrame(() => {
      const scrollTarget = resultsList.querySelector('.product-grid__item[data-product-id]') ?? resultsList;
      const cssLength = (/** @type {string} */ property) => {
        const raw = getComputedStyle(document.body).getPropertyValue(property).trim();
        if (!raw) return undefined;
        const parsed = parseFloat(raw);
        return Number.isFinite(parsed) ? parsed : undefined;
      };
      const stickyHeaderOffset =
        cssLength('--sticky-header-height') ?? cssLength('--header-height') ?? 0;
      const targetTop = scrollTarget.getBoundingClientRect().top + window.scrollY - stickyHeaderOffset - 16;

      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      });
    });
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
    this.#collapsePanelUnlessMultiSelect();
  }

  /**
   * Collapses this filter's panel after a value is picked.
   *
   * A section re-render preserves the open state now that every facet accordion carries a stable
   * id (see the comment in `list-filter.liquid`), so a panel closes only because we close it here.
   * Multi-select filters opt out through `data-multi-select` and stay open, so several values can
   * be checked in a row without reopening the panel between clicks.
   *
   * Runs before the section request resolves, so the morph copies the collapsed state over
   * instead of the panel visibly closing after the results come back.
   */
  #collapsePanelUnlessMultiSelect() {
    const details = this.closest('details');
    if (!(details instanceof HTMLDetailsElement)) return;
    if (details.dataset.multiSelect === 'true') return;

    details.open = false;
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

class CategoryCollectionFilterComponent extends Component {
  changeCategory(event) {
    const target = event.composedPath()[0];
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.checked) return;

    const categoryUrlInput = target.closest('li')?.querySelector('input[data-category-url-for]');
    if (!(categoryUrlInput instanceof HTMLInputElement) || !categoryUrlInput.value) return;

    const targetUrl = new URL(categoryUrlInput.value, window.location.origin);
    const currentParameters = new URLSearchParams(window.location.search);

    currentParameters.delete('page');
    currentParameters.delete('section_id');
    currentParameters.delete('category');
    currentParameters.delete('category-url');
    targetUrl.search = '';

    for (const [param, value] of currentParameters.entries()) {
      targetUrl.searchParams.append(param, value);
    }

    window.location.assign(targetUrl.toString());
  }
}

if (!customElements.get('category-collection-filter-component')) {
  customElements.define('category-collection-filter-component', CategoryCollectionFilterComponent);
}

/**
 * Minor units per major unit. Matches the `divided_by: 100` used in price-filter.liquid.
 * Zero-decimal currencies (JPY) and three-decimal ones (KWD) are not supported, as
 * documented in the snippet.
 */
const MINOR_UNITS_PER_MAJOR = 100;

/**
 * Attributes the section morph rewrites when price filter state changes on the server:
 * `value` on the hidden inputs, the bounds on the component itself. Watching these is how
 * the slider learns about a clear, a removed pill, or a shifted `range_max`.
 */
const SERVER_STATE_ATTRIBUTES = ['value', 'data-range-max', 'data-step'];

/**
 * @typedef {Object} PriceRange
 * @property {number} min - The lower bound in major units
 * @property {number} max - The upper bound in major units
 */

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The hidden minimum price input (major units)
 * @property {HTMLInputElement} maxInput - The hidden maximum price input (major units)
 * @property {HTMLInputElement} rangeMin - The lower slider thumb
 * @property {HTMLInputElement} rangeMax - The upper slider thumb
 * @property {HTMLInputElement} fieldMin - The lower numeric field
 * @property {HTMLInputElement} fieldMax - The upper numeric field
 * @property {HTMLElement} slider - The slider wrapper carrying the fill custom properties
 */

/**
 * Handles price facet functionality.
 *
 * The hidden `minInput`/`maxInput` are the single source of truth: they are the only
 * elements the facets form serialises. The slider and the numeric fields are projections
 * of them and carry no `name`, so locale-formatted text never reaches the URL.
 *
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  requiredRefs = ['minInput', 'maxInput', 'rangeMin', 'rangeMax', 'fieldMin', 'fieldMax', 'slider'];

  /** Guards against the morph resetting a control the user is currently operating. */
  #isInteracting = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerdown', this.#beginInteraction);
    this.addEventListener('focusin', this.#beginInteraction);
    this.addEventListener('focusout', this.#endInteraction);
    this.addEventListener('keydown', this.#handleKeyDown);
    document.addEventListener('pointerup', this.#endInteraction);
    document.addEventListener('pointercancel', this.#endInteraction);

    this.#serverStateObserver.observe(this, {
      attributes: true,
      attributeFilter: SERVER_STATE_ATTRIBUTES,
      subtree: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('pointerup', this.#endInteraction);
    document.removeEventListener('pointercancel', this.#endInteraction);
    this.#serverStateObserver.disconnect();
  }

  /**
   * The morph reaches the hidden inputs and this element's own attributes, but not the
   * visible controls, which opt out via `data-skip-node-update`. `updatedCallback` would be
   * the natural place to bridge that gap, except it never fires: morph.js hands
   * `onAfterUpdate` the node parsed out of the response, where custom elements are not
   * upgraded, so its `instanceof Component` check is always false. Watching the attributes
   * the morph does touch is the reliable signal, and it keeps the fix local to this
   * component instead of changing morph behaviour for the whole theme.
   *
   * Only server writes trip this: the sync assigns `value` properties and inline styles,
   * neither of which is an observed attribute, so there is no feedback loop.
   */
  #serverStateObserver = new MutationObserver(() => {
    // The morph emits a burst of mutations; collapse them into a single sync.
    if (this.#syncQueued) return;
    this.#syncQueued = true;

    queueMicrotask(() => {
      this.#syncQueued = false;
      this.#syncFromServer();
    });
  });

  #syncQueued = false;

  /** Kept for the day morph.js starts passing the live node to `onAfterUpdate`. */
  updatedCallback() {
    super.updatedCallback();
    this.#syncFromServer();
  }

  /**
   * Reapplies server state to the visible controls: bounds from this element's attributes,
   * values from the hidden inputs.
   */
  #syncFromServer() {
    if (this.#isInteracting || !this.isConnected) return;

    const { rangeMin, rangeMax, fieldMin, fieldMax, minInput, maxInput } = this.refs;
    if (!rangeMin || !rangeMax || !fieldMin || !fieldMax || !minInput || !maxInput) return;

    const bound = this.#rangeMax;

    for (const thumb of [rangeMin, rangeMax]) {
      thumb.min = '0';
      thumb.max = String(bound);
      thumb.step = String(this.#step);
    }

    const min = this.#parseMoney(minInput.value) ?? 0;
    const max = this.#parseMoney(maxInput.value) ?? bound;

    rangeMin.value = String(min);
    rangeMax.value = String(max);
    fieldMin.value = minInput.value ? String(min) : '';
    fieldMax.value = maxInput.value ? String(max) : '';

    this.#paint({ min, max });
  }

  /**
   * Reflects an in-progress interaction without hitting the network.
   * @param {Event} event - The input event
   */
  previewPriceFilter(event) {
    const source = event.composedPath()[0];
    if (!(source instanceof HTMLInputElement)) return;

    const { fieldMin, fieldMax } = this.refs;

    if (source === fieldMin || source === fieldMax) {
      this.#previewTypedValue(source);
      return;
    }

    const range = this.#collect(source);
    if (!range) return;

    this.#reflect(range, source);
  }

  /**
   * Previews a half-typed field value.
   *
   * A number arrives one digit at a time, so "3" on the way to "3000" is briefly a real
   * value that happens to sit below the lower bound. Running the thumb reconciliation on it
   * would push the opposite bound down and overwrite what the shopper typed earlier, so
   * while typing we only move this field's own thumb and leave the other bound alone.
   * Reconciliation happens once on commit, where the number is complete.
   *
   * @param {HTMLInputElement} source - The field being typed into
   */
  #previewTypedValue(source) {
    const { rangeMin, rangeMax, fieldMin } = this.refs;
    const parsed = this.#parseMoney(source.value);

    // Empty or not yet a number: hold the current visuals until it becomes one.
    if (parsed === null) return;

    const thumb = source === fieldMin ? rangeMin : rangeMax;
    thumb.value = String(this.#clamp(parsed));

    // Repaint only while the two bounds still describe a real range; a crossed pair is a
    // transient state of the keystroke, not something to render.
    const min = Number(rangeMin.value);
    const max = Number(rangeMax.value);
    if (min <= max) this.#paint({ min, max });
  }

  /**
   * Commits the range and refreshes the results. Fires on thumb release and on field blur,
   * so a whole drag costs one request and one history entry.
   * @param {Event} event - The change event
   */
  updatePriceFilterAndResults(event) {
    const source = event.composedPath()[0];
    if (!(source instanceof HTMLInputElement)) return;

    const range = this.#collect(source);
    if (!range) return;

    this.#reflect(range, source, { normalizeSource: true });
    this.#writeHidden(range);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSummary();
  }

  /**
   * Returns the facet to its unfiltered state. Called by FacetClearComponent, which cannot
   * simply blank the inputs: the thumbs have to travel back to the ends of the track.
   */
  reset() {
    const { minInput, maxInput, rangeMin, rangeMax, fieldMin, fieldMax } = this.refs;
    const bound = this.#rangeMax;

    minInput.value = '';
    maxInput.value = '';
    fieldMin.value = '';
    fieldMax.value = '';
    rangeMin.value = '0';
    rangeMax.value = String(bound);

    this.#paint({ min: 0, max: bound });
  }

  /** @returns {number} The upper bound of the track in major units */
  get #rangeMax() {
    return Number(this.dataset.rangeMax) || 0;
  }

  /** @returns {number} The slider increment in major units */
  get #step() {
    return Number(this.dataset.step) || 1;
  }

  /** @returns {string} The active currency code */
  get #currency() {
    return this.dataset.currency || '';
  }

  /**
   * Derives the range from whichever control the user touched, clamped and ordered.
   * @param {HTMLInputElement} source - The control that emitted the event
   * @returns {PriceRange | null} The resolved range, or null if the event came from elsewhere
   */
  #collect(source) {
    const { rangeMin, rangeMax, fieldMin, fieldMax } = this.refs;
    const bound = this.#rangeMax;
    const fromThumb = source === rangeMin || source === rangeMax;
    const fromField = source === fieldMin || source === fieldMax;

    if (!fromThumb && !fromField) return null;

    let min;
    let max;

    if (fromThumb) {
      min = Number(rangeMin.value);
      max = Number(rangeMax.value);
    } else {
      // Snap typed values onto the step grid: down for the lower bound, up for the upper
      // one, so a hand-typed range never narrows past what the user asked for.
      min = this.#snap(this.#parseMoney(fieldMin.value) ?? 0, 'down');
      max = this.#snap(this.#parseMoney(fieldMax.value) ?? bound, 'up');
    }

    min = this.#clamp(min);
    max = this.#clamp(max);

    // Push the opposite bound rather than swapping, so the thumb under the cursor stays put.
    if (min > max) {
      if (source === rangeMin || source === fieldMin) {
        max = min;
      } else {
        min = max;
      }
    }

    return { min, max };
  }

  /**
   * Mirrors the resolved range onto the controls the user is not currently editing.
   * @param {PriceRange} range - The resolved range
   * @param {HTMLInputElement} source - The control that emitted the event
   * @param {Object} [options] - Additional options
   * @param {boolean} [options.normalizeSource=false] - Whether to rewrite the source field too
   */
  #reflect(range, source, options = {}) {
    const { normalizeSource = false } = options;
    const { rangeMin, rangeMax, fieldMin, fieldMax } = this.refs;

    // Thumbs are always safe to rewrite; text fields only once the user has finished,
    // otherwise the caret jumps mid-typing.
    rangeMin.value = String(range.min);
    rangeMax.value = String(range.max);

    if (normalizeSource || source !== fieldMin) fieldMin.value = String(range.min);
    if (normalizeSource || source !== fieldMax) fieldMax.value = String(range.max);

    this.#paint(range);
  }

  /**
   * Writes the committed range to the hidden inputs.
   *
   * A bound that sits at the end of the track is written as an empty string so the
   * parameter drops out of the URL entirely — this is what keeps an untouched slider from
   * pinning the results to a range and what lets `range_max` grow again later.
   *
   * @param {PriceRange} range - The committed range
   */
  #writeHidden(range) {
    const { minInput, maxInput } = this.refs;

    minInput.value = range.min > 0 ? String(range.min) : '';
    maxInput.value = range.max < this.#rangeMax ? String(range.max) : '';
  }

  /**
   * Positions the track fill. Driven by values rather than measured pixels, so a slider
   * inside a collapsed `<details>` or an unopened popover paints correctly on first reveal.
   * @param {PriceRange} range - The range to paint
   */
  #paint(range) {
    const bound = this.#rangeMax;
    if (bound <= 0) return;

    const { slider } = this.refs;

    slider.style.setProperty('--range-from', `${(range.min / bound) * 100}%`);
    slider.style.setProperty('--range-to', `${(range.max / bound) * 100}%`);
  }

  /**
   * Parses a user- or server-supplied money string into major units.
   * @param {string} value - The raw value
   * @returns {number | null} The value in major units, or null when unparseable
   */
  #parseMoney(value) {
    if (!value || !value.trim()) return null;

    const minorUnits = convertMoneyToMinorUnits(value, this.#currency);
    if (minorUnits === null) return null;

    return minorUnits / MINOR_UNITS_PER_MAJOR;
  }

  /**
   * Snaps a value onto the step grid.
   * @param {number} value - The value in major units
   * @param {'up' | 'down'} direction - Which way to round
   * @returns {number} The snapped value
   */
  #snap(value, direction) {
    const step = this.#step;
    const rounded = direction === 'up' ? Math.ceil(value / step) : Math.floor(value / step);

    return rounded * step;
  }

  /**
   * Constrains a value to the track.
   * @param {number} value - The value in major units
   * @returns {number} The clamped value
   */
  #clamp(value) {
    if (!Number.isFinite(value)) return 0;

    return Math.min(Math.max(value, 0), this.#rangeMax);
  }

  #beginInteraction = () => {
    this.#isInteracting = true;
  };

  #endInteraction = () => {
    this.#isInteracting = false;
  };

  /**
   * Keeps the numeric fields to digits and separators, and stops Enter from submitting the
   * facets form — blurring instead, which routes through the normal commit path.
   * @param {KeyboardEvent} event - The keydown event
   */
  #handleKeyDown = (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;

    const target = event.composedPath()[0];
    const { fieldMin, fieldMax } = this.refs;

    if (target !== fieldMin && target !== fieldMax) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      if (target instanceof HTMLElement) target.blur();
      return;
    }

    const allowed = /^([0-9]|\.|,|Tab|Backspace|Delete|Escape|Home|End|Arrow(Up|Down|Left|Right))$/;
    if (!allowed.test(event.key)) event.preventDefault();
  };

  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');
    if (!(statusComponent instanceof FacetStatusComponent)) return;
    statusComponent.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');

    if (container instanceof PriceFacetComponent) {
      // Blanking the inputs is not enough for a slider: an empty value parks both thumbs at
      // zero instead of returning them to the ends of the track.
      container.reset();
    } else {
      container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
          input.value = '';
        }
      });
    }

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      clearButton.classList.toggle(activeClass, event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ': {
        const targetOption = document.activeElement?.closest('[role="option"]');
        if (targetOption && listbox.contains(targetOption)) {
          event.preventDefault();
          this.#selectOption(targetOption);
        }
        break;
      }

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        const span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = inputElement.getAttribute('aria-label') ?? '';
        return (swatch?.outerHTML ?? '') + span.outerHTML;
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.textContent = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}
